import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { WorkspaceFolderLiteData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Crumbs } from '../common/Crumbs';
import { EnableIfHasFeature, HideIfHasFeature, useFeatureCheck } from '../common/FeatureElements';
import { Page } from '../common/Page';
import { FolderModel, WorkspaceModel } from '../dashboard/Models';
import { Flexer, Title } from '../dashboard/elements/LayoutElements';
import { XmasLightsDashboard } from '../occasions/xmas/XmasLightsDashboard';
import { Box, Colors, Menu } from './../../ui';
import { boardRestore } from './BoardCommands';
import { FolderBoardView } from './FolderBoardView';
import { folderDelete, folderRename } from './FolderCommands';
import { WorkspaceFeatureCallout } from './WorkspaceFeatureCallout';
import { WorkspacePageContent } from './WorkspacePageContent';
import { WorkspaceStripMenu } from './WorkspaceStripMenu';

const FolderMenu: LFC<{ folder: WorkspaceFolderLiteData, onEdit: VoidCallback }> = ({ folder, onEdit }) => {
    const dispatch = useResolve(CommandDispatcher);
    const profile = useResolve(ProfileModel);
    const [open, setOpen] = useState(false);
    const close = () => {
        setOpen(false);
        return true;
    };
    const onDelete = useCallback(() => dispatch(folderDelete, [folder.id]), [folder]);
    const onRestoreBoard = useCallback(() => close() && dispatch(boardRestore), []);

    return <Crumbs.Menu label={folder.label} open={open} setOpen={setOpen}>
        <Box minWidth="12">
            <Menu>
                {/* {profile.flags.super && <>
                    <Menu.Item onClick={onRestoreBoard}><NoIcon size="1.2" />Restore Backup</Menu.Item>
                    <Menu.Divide />
                </>} */}
                <Menu.Item onClick={onEdit}>
                    <Icon size="1.2" src="deqo://icons/hero/pencil-alt" />
                    <Menu.ItemText>
                        Rename folder
                    </Menu.ItemText>
                </Menu.Item>
                <Menu.Item onClick={onDelete}>
                    <Icon size="1.2" src="deqo://icons/hero/trash" />
                    <Menu.ItemText>
                        Delete folder
                    </Menu.ItemText>
                </Menu.Item>
            </Menu>
        </Box>
    </Crumbs.Menu>;
};

const FolderCrumbs: LFC<{ folder: FolderModel }> = (props) => {

    const dispatch = useResolve(CommandDispatcher);
    const [editing, setEditing] = useState(false);

    const workspace = useObserver(useResolve(WorkspaceModel));
    const folder = useObserver(props.folder);
    const canEdit = useFeatureCheck('workspaces') && workspace.joined;

    const onApply = (value: string) => {
        setEditing(false);
        dispatch(folderRename, [folder.id, value]);
    };

    const onCancel = () => setEditing(false);

    return <Crumbs items={[[workspace.label, `~/${workspace.alias}`]]}>
        {canEdit && !editing &&
            <FolderMenu folder={folder} onEdit={() => setEditing(true)} />
        }
        {!canEdit &&
            <Crumbs.Title>{folder.label}</Crumbs.Title>
        }
        {editing &&
            <Crumbs.Input initialValue={folder.label} onApply={onApply} onCancel={onCancel} />
        }
    </Crumbs>;
};

const FolderEmpty = () => {
    return <Flexer inset flex center pointerOff gap=".75">
        <Title color={Colors.gray400} flush size="3xl">There is nothing here yet!</Title>
        <Title color={Colors.gray400} size="lg">Create a board with the buttons above.</Title>
    </Flexer>;
};

export const FolderView: LFC<{ folderId: string }> = ({ folderId }) => {

    const workspace = useResolve(WorkspaceModel);
    const folder = workspace.folders.find(x => x.id === folderId);
    if (!folder) return null;

    const { joined, type } = useObserver(workspace);
    const { boards } = useObserver(folder);

    const ConfiguredBoardCreateStripMenu = useMemo(() => () => <WorkspaceStripMenu folders={false} templates={type !== 'library'} />, [type]);

    return <>
        <UseLocationScope name="folder" value={folder.id} label={folder.label} />
        <Page.Header>
            <FolderCrumbs folder={folder} />
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <EnableIfHasFeature name="workspaces" flex grow>
            <WorkspacePageContent top={ConfiguredBoardCreateStripMenu} showSidebar={type !== 'user'}>
                <XmasLightsDashboard />
                {!!boards.length && <FolderBoardView {...{ actions: joined, boards }} />}
                {!boards.length && <FolderEmpty />}
            </WorkspacePageContent>
        </EnableIfHasFeature>
        <HideIfHasFeature name="workspaces">
            <WorkspaceFeatureCallout />
        </HideIfHasFeature>
    </>;
};
